import React, { useEffect, useState } from 'react';
import { Briefcase, CheckCircle, AlertCircle, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { jobBoardApi } from '../../api/jobBoardApi';
import { JobBoardStats } from '../../types/jobBoard';
import Spinner from '../../components/Spinner';

const JobsOverview = () => {
  const [stats, setStats] = useState<JobBoardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await jobBoardApi.getOverviewStats();
        setStats(res.data);
      } catch (err) {
        console.error("Error fetching job stats:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) return <div className="p-8 flex justify-center"><Spinner /></div>;

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto w-full">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
            <Briefcase className="text-indigo-600" size={24} />
            Jobs Management
          </h1>
          <p className="text-slate-500 text-sm mt-1">Overview of your job inventory and performance.</p>
        </div>
        <Link to="/job-board/jobs/create" className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl hover:bg-indigo-700 transition shadow-sm flex items-center gap-2 font-semibold">
          <Plus size={18} /> New Posting
        </Link>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {[
          { label: 'Total Jobs', value: stats?.totalJobs.toString() || '0', icon: Briefcase, color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Active Postings', value: stats?.activeJobs.toString() || '0', icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Open Vacancies', value: stats?.jobsNeedingApplicants.toString() || '0', icon: AlertCircle, color: 'text-rose-600', bg: 'bg-rose-50' },
        ].map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
            <div className={`w-10 h-10 ${stat.bg} rounded-xl flex items-center justify-center mb-4`}>
              <stat.icon className={stat.color} size={20} />
            </div>
            <p className="text-sm font-medium text-slate-400">{stat.label}</p>
            <h3 className="text-2xl font-bold text-slate-800 mt-1">{stat.value}</h3>
          </div>
        ))}
      </div>

      <div className="bg-indigo-900 rounded-3xl p-8 text-white relative overflow-hidden">
        <div className="relative z-10 max-w-lg">
          <h2 className="text-2xl font-bold mb-2">Grow your team faster.</h2>
          <p className="text-indigo-100 mb-6 italic opacity-80">
            "The job board module helped us reduce our time-to-hire by 40% in the first quarter."
          </p>
          <Link to="/job-board/jobs" className="bg-white text-indigo-900 px-6 py-2 rounded-xl font-bold hover:bg-indigo-50 transition inline-block">
            Manage Postings
          </Link>
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500 rounded-full blur-3xl opacity-20 -mr-20 -mt-20"></div>
      </div>
    </div>
  );
};

export default JobsOverview;